import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { 
  EmailSignupBox, 
  TermsFooter, 
  mentalMintLogo, 
  Page, 
  CommonPageId,
  pageRegistry,
  commonPagesRegistry,
  RootState,
  initializeSession,
  witocracyIconLarge
} from '@mental-mint/shared-ui';
import { getPlayer, useMagicLinkAuth } from '@mental-mint/shared-utils';
import './LandingPage.css';
import itchioIcon from '../assets/itchio-textless-white.svg';
import discordIcon from '../assets/Discord-Symbol-White.svg';

/**
 * Mental Mint Landing Page
 * 
 * Main landing page that showcases all Mental Mint games.
 * Features:
 * - Modern hero section with CTA
 * - Key features showcase
 * - Game showcase grid
 * - Statistics section
 * - Email signup
 * - Creator message
 * - Terms & Privacy footer
 */
export const LandingPage: React.FC = () => {
  const dispatch = useDispatch();
  const { isInitialized } = useSelector((state: RootState) => state.gameSession);
  const [isMissionExpanded, setIsMissionExpanded] = useState(false);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [isGameSnapActive, setIsGameSnapActive] = useState(true);
  const isGameSnapActiveRef = useRef(true);
  const landingPageRef = useRef<HTMLDivElement>(null);
  const comingSoonRef = useRef<HTMLDivElement>(null);
  const taptionaryRef = useRef<HTMLDivElement>(null);

  // Handle magic link authentication if token in URL
  useMagicLinkAuth({ dispatch });

  // Initialize minimal game session for landing page
  useEffect(() => {
    if (!isInitialized) {
      const player = getPlayer();
      dispatch(initializeSession({
        gameId: 'landing' as any,
        game: {
          metadata: {
            gameId: 'landing' as any,
            name: 'Mental Mint',
            description: 'Mental Mint Games Landing Page',
            launchStatus: 'launched' as any,
          },
          colorPrimary: '#10b981',
          colorSecondary: '#ffffff',
          fontPrimary: 'inherit',
        } as any,
        player,
      }));
    }
  }, [dispatch, isInitialized]);

  // Register common pages on mount
  useEffect(() => {
    const commonPageRenderers = commonPagesRegistry.getPageRenderers();
    pageRegistry.registerPages(commonPageRenderers);
  }, []);

  // Prevent iframes from stealing focus and scrolling the page on load
  useEffect(() => {
    if (landingPageRef.current) {
      landingPageRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const landingPage = landingPageRef.current;
    const comingSoon = comingSoonRef.current;

    if (!landingPage || !comingSoon) {
      return;
    }

    let disableSnapTimeout: number | undefined;
    let previousScrollTop = landingPage.scrollTop;

    const updateSnapState = () => {
      const isScrollingUp = landingPage.scrollTop < previousScrollTop;
      const isAboveComingSoon = comingSoon.getBoundingClientRect().top > landingPage.getBoundingClientRect().top;
      previousScrollTop = landingPage.scrollTop;

      window.clearTimeout(disableSnapTimeout);

      if (isAboveComingSoon) {
        if (!isGameSnapActiveRef.current && isScrollingUp) {
          isGameSnapActiveRef.current = true;
          setIsGameSnapActive(true);
          requestAnimationFrame(() => taptionaryRef.current?.scrollIntoView({ behavior: 'smooth' }));
        }
        return;
      }

      disableSnapTimeout = window.setTimeout(() => {
        const hasSettledAtComingSoon = comingSoon.getBoundingClientRect().top <= landingPage.getBoundingClientRect().top;
        if (hasSettledAtComingSoon) {
          isGameSnapActiveRef.current = false;
          setIsGameSnapActive(false);
        }
      }, 150);
    };

    updateSnapState();
    landingPage.addEventListener('scroll', updateSnapState, { passive: true });
    window.addEventListener('resize', updateSnapState);

    return () => {
      window.clearTimeout(disableSnapTimeout);
      landingPage.removeEventListener('scroll', updateSnapState);
      window.removeEventListener('resize', updateSnapState);
    };
  }, []);

  const handleTermsClick = () => {
    setCurrentPageId(CommonPageId.Terms);
  };

  const handleClosePage = () => {
    setCurrentPageId(null);
  };

  const scrollToGames = () => {
    const gamesSection = document.getElementById('games-section');
    gamesSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToNewsletter = () => {
    const newsletterSection = document.getElementById('newsletter-section');
    isGameSnapActiveRef.current = false;
    setIsGameSnapActive(false);
    requestAnimationFrame(() => newsletterSection?.scrollIntoView({ behavior: 'smooth' }));
  };

  return (
    <div
      ref={landingPageRef}
      className={`landing-page${isGameSnapActive ? ' landing-page--snap-games' : ''}`}
    >
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-background"></div>
        <div className="landing-hero-content">
          <motion.div
            className="landing-logo-container"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <img 
              src={mentalMintLogo} 
              alt="Mental Mint Logo" 
              className="landing-logo"
            />
          </motion.div>
          
          <motion.h1 
            className="landing-hero-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="green-emphasis">Mental Mint</span> Games
          </motion.h1>
          
          <motion.p 
            className="landing-hero-subtitle"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Daily games that make microlearning fun
          </motion.p>

          <motion.div 
            className="landing-hero-cta"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <button className="landing-cta-button primary" onClick={scrollToGames}>
              <Lucide.Gamepad2 size={20} />
              Play Free Games
            </button>
            <button className="landing-cta-button secondary" onClick={scrollToNewsletter}>
              <Lucide.Mail size={20} />
              Get Updates
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Content - Games Showcase */}
      <main className="landing-main" id="games-section">
        <div className="landing-container">
          <div className="playable-games-section">
            {/* Tile Clicker */}
            <motion.div
              className="game-embed-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <iframe
                src="https://tileclicker.com"
                className="game-embed-iframe"
                title="Tile Clicker"
                loading="lazy"
                allow="fullscreen"
                scrolling="no"
                tabIndex={-1}
              />
            </motion.div>

            {/* Analogy Ascent */}
            <motion.div
              className="game-embed-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <iframe
                src="https://analogyascent.com"
                className="game-embed-iframe"
                title="Analogy Ascent"
                loading="lazy"
                allow="fullscreen"
                scrolling="no"
                tabIndex={-1}
              />
            </motion.div>

            {/* Taptionary */}
            <motion.div
              ref={taptionaryRef}
              className="game-embed-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <iframe
                src="https://taptionary.com"
                className="game-embed-iframe"
                title="Taptionary"
                loading="lazy"
                allow="fullscreen"
                scrolling="no"
                tabIndex={-1}
              />
            </motion.div>
          </div>

          {/* Coming Soon Section */}
          <motion.div
            ref={comingSoonRef}
            className="coming-soon-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="landing-section-title" style={{ marginBottom: '2rem' }}>
              <Lucide.Sparkles size={32} className="title-icon" />
              Coming Soon
            </h2>
            <div className="coming-soon-grid">
              <div className="coming-soon-card">
                <img src={witocracyIconLarge} alt="Banter Battle icon" className="coming-soon-icon" />
                <div className="coming-soon-info">
                  <div className="coming-soon-name-row">
                    <h3 className="coming-soon-name">Banter Battle</h3>
                    <span className="coming-soon-badge">Coming Soon</span>
                  </div>
                  <p className="coming-soon-description">Reddit meets Quiplash. Vote on the world's wittiest answers and submit your own.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Features Section */}
      <section className="landing-features">
        <div className="landing-container">
          <motion.div 
            className="landing-features-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <Lucide.DollarSign size={32} />
              </div>
              <h3>100% Free</h3>
              <p>No ads, no paywalls, no microtransactions. Just pure gaming fun</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <Lucide.Calendar size={32} />
              </div>
              <h3>Daily Puzzles</h3>
              <p>Fresh challenges every day to keep your mind sharp</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <Lucide.Zap size={32} />
              </div>
              <h3>Instant Play</h3>
              <p>No downloads, no waiting. Start playing directly in your browser</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <Lucide.Smartphone size={32} />
              </div>
              <h3>Mobile-Friendly</h3>
              <p>Play anywhere, on any device, at any time</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats / Accolades Section */}
      <section className="landing-stats">
        <div className="landing-container">
          <div className="landing-stats-grid">
            <div className="landing-stat-item">
              <div className="landing-stat-icon">
                <Lucide.Award size={32} />
              </div>
              <div className="landing-stat-number">Award-Winning</div>
              <div className="landing-stat-label">Game Jam & Indie Festival Winner</div>
            </div>
            <div className="landing-stat-item">
              <div className="landing-stat-icon">
                <Lucide.Twitch size={32} />
              </div>
              <div className="landing-stat-number">Ex-Twitch</div>
              <div className="landing-stat-label">Minigame Developer at Twitch Prime</div>
            </div>
            <div className="landing-stat-item">
              <div className="landing-stat-icon">
                <Lucide.Gamepad2 size={32} />
              </div>
              <div className="landing-stat-number">4 Games</div>
              <div className="landing-stat-label">Unique Brain-Training Experiences</div>
            </div>
            <div className="landing-stat-item">
              <div className="landing-stat-icon">
                <Lucide.Heart size={32} />
              </div>
              <div className="landing-stat-number">Indie Dev</div>
              <div className="landing-stat-label">Passion-Driven Solo Creator</div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="landing-newsletter-outer" id="newsletter-section">
        <div className="landing-container">
          <motion.div
            className="landing-newsletter-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <EmailSignupBox />
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-content">
            
            <div className="landing-footer-links">
              <TermsFooter onTermsClick={handleTermsClick} />
            </div>

            <div className="landing-footer-social">
              <p className="footer-social-label">Follow Us</p>
              <div className="footer-social-icons">
                <a href="https://www.instagram.com/mental.mint.games" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                  <Lucide.Instagram size={20} />
                </a>
                <a href="https://nkdc.itch.io/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Itch.io">
                  <img src={itchioIcon} alt="Itch.io" style={{ width: '20px', height: '20px' }} />
                </a>
                <a href="https://discord.gg/DR9b7A4K4p" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Discord">
                  <img src={discordIcon} alt="Discord" style={{ width: '20px', height: '20px' }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Page System - Renders modals like Terms */}
      {currentPageId && (
        <Page
          pageId={currentPageId}
          config={{
            id: currentPageId,
            modalPageConfig: {
              humanReadableTitle: "Terms of Service & Privacy Policy"
            },
            navItem: null
          }}
          onRequestClose={handleClosePage}
        />
      )}
    </div>
  );
};

export default LandingPage;
